/**
 * STORE-SPLIT slice 3 — the two `File`-handle DXF import variants
 * (`importDxfFileAsync` — AutoCAD INSERT-style streaming merge; and
 * `importDxfFileAsyncReplacing` — AutoCAD OPEN-style streaming replace),
 * extracted VERBATIM from `editorStore.ts` (no behaviour change; the same
 * STORE-SPLIT lineage as slice 1 `editorStoreStyleTables.ts` and slice 2
 * `editorStoreImportActions.ts`). The slice-2 module JSDoc named this the
 * "planned slice 3 (import-core now, variants later)".
 *
 * `editorStore.ts` composes the slice into its `create()` initializer via
 * `...createDxfImportFileActionsSlice(set, get, deps)` — the standard zustand
 * slice pattern. `set`/`get` ARE the store's `setState`/`getState` (zustand
 * passes the same function references into the initializer), so the former
 * `useEditorStore.getState()` call sites inside the action bodies are
 * byte-identical in behaviour as `get()` (the ONLY textual edit made during the
 * move; every other line is verbatim).
 *
 * ## Deps injection (shared machinery stays with its owner)
 *
 * `rebuildEntityMetadataIndexFromImportAsync` bumps the REACTIVE
 * `entityMetadataIndexVersion` / `renderTopologyVersion` counters via
 * `useEditorStore.setState`, so it remains a module-level export of
 * `editorStore.ts` (moving it here would create a back-edge cycle). The factory
 * receives it via `deps` — the compose site passes the same function reference
 * the inline body called before. Destructured at the top of the factory so the
 * method bodies stay verbatim.
 *
 * ## Deferred (NOT this PR)
 *
 * The two methods share a near-identical streaming worker-chunk consumer +
 * commit `set()` shape with each other and with the slice-2 text variants
 * (`buildImportMergeCommitSlice` / `buildImportMergeSuccessResult` in
 * `editorStoreImportActions.ts`). Folding this shared body onto those one-truth
 * helpers is a BEHAVIOUR-TOUCHING refactor (the OPEN variant mints a new
 * documentEpoch and hydrates sysvars unconditionally, unlike the INSERT merge),
 * so it is deliberately DEFERRED — this PR is a pure verbatim move (category B).
 * The module stays >1000 lines (jscpd `maxLines: 1000` skips it, exactly as the
 * bodies were unscanned inside the 6740-line `editorStore.ts`); the file-size
 * split into a shared streaming helper is the natural follow-up once the
 * consolidation lands.
 *
 * ## Function dependency map
 *
 * createDxfImportFileActionsSlice(set, get, deps)
 *   importDxfFileAsync (INSERT-style streaming merge into the current drawing)
 *     -> startDxfImportFromFileInWorker (dxfWorkerClient.ts) | main-thread fallback
 *     -> appendBatchToCompactCacheSilent / markSpaceStreamed / markCachePopulated
 *        (mutationStore.ts — streaming chunk consumer)
 *     -> computeCoordinateOriginForImport -> setCoordinateOrigin (first import)
 *     -> mergeImportedGeometryDocumentAsync / healImportedDocument
 *     -> populateCompactCacheFromDocumentAsync
 *     -> set(commit slice) [INSERT: DUAL-BUMP geometry+table, no epoch mint]
 *     -> deps.rebuildEntityMetadataIndexFromImportAsync
 *     -> populateDependencyIndexFromDocument / finalizeStreamedViewportMembership
 *     -> hydrateSysvars/CeColor (empty-seed only)
 *   importDxfFileAsyncReplacing (OPEN-style streaming replace)
 *     -> same worker/chunk pipeline, fresh doc id + dropDocumentHolder(previousDocId)
 *     -> set(commit slice) [OPEN: mintNewDocumentEpoch, hydrate unconditionally]
 *     -> dropDocumentProvenance(previousDocId) only once the swap above lands
 *        (PR #582 — a failed/aborted parse leaves the outgoing document's
 *        provenance ledger intact, since it's still the one on screen)
 */
import {
  bumpForClasses,
  computeGlobalPercent,
  mintNewDocumentEpoch,
  type BumpClassSet,
  type DrawingDocument,
  type DrawingEntity,
  type ImportPhaseName,
  type ReferenceFix,
  type RevisionCounters,
} from "@dxf-ai-editor/drawing-model";
import { decodeDxfBytes } from "@dxf-ai-editor/dxf-io";
import { type EditorCommand, type EditorShellState } from "@dxf-ai-editor/contracts";
import { t, getActiveLanguage } from "../i18n";
import { pluralFormFor } from "../i18n/pluralForm";
// COLLAB-LOCK-COMMAND — P2 (post-review, round 5): the pure fail-closed
// EDIT veto predicate `dispatch()` uses (see `editorStore.ts`'s "COLLAB-
// LOCK-COMMAND — fail-closed EDIT veto" comment). `importDxfFileAsync`
// below commits directly via `set()` (the documented import/bootstrap
// exception to the `dispatch()`-mediated write path, ПРИОРИТЕТ #2.1) so it
// never reaches this check through the normal call site — it must run its
// own.
import { evaluateCollabLockVeto } from "./collabLockVeto";
import {
  importGeometryDocumentFromDxfTextWithProgress,
  type DxfImportResult,
} from "./dxfGeometry";
import {
  isWorkerSupported,
  startDxfImportFromFileInWorker,
} from "./dxfWorkerClient";
import {
  appendBatchToCompactCacheSilent,
  clearCompactCache,
  clearEntityProvenance,
  DEFAULT_DOCUMENT_ID,
  dropDocumentHolder,
  dropDocumentProvenance,
  entityMetadataIndex,
  markCachePopulated,
  markSpaceStreamed,
  peekDocumentMemoryHolder,
  populateCompactCacheFromDocumentAsync,
  populateDependencyIndexFromDocument,
  setActiveDocument,
} from "./mutationStore";
import {
  buildStreamingHealResult,
  healImportedDocument,
  type ImportHealResult,
} from "./referenceHealWiring";
import {
  mergeImportedGeometryDocumentAsync,
  syncShellStateFromMergedGeometryDocument,
} from "./storeHelpers";
import { pushAuditEvent } from "./reducerHelpers";
// COLLAB-LOCK-COMMAND (post-review, round 4) — a TRUE document SWAP (this
// is the OPEN-a-different-file branch, the `mintNewDocumentEpoch` site just
// below) must drop any live collab-lock connection: the companion room is
// keyed by the ORIGINAL `documentId` from `connect()`'s hello, so continuing
// to trust its `locks` map against a DIFFERENT now-active document would let
// the fail-closed veto both false-positive (an unrelated same-named layer in
// the NEW document reads as locked) and false-negative (a genuinely locked
// scope in the NEW document has no corresponding server room at all). See
// `collabLockStore.ts`'s `disconnect()` — idempotent/safe to call even when
// already disconnected (INV-OFF: this PR ships no auto-connect UI, so this
// is defense-in-depth for whenever a future S-COLLAB-UI slice wires one up).
import { useCollabLockStore } from "./collabLockStore";
import {
  getCoordinateOrigin,
  setCoordinateOrigin,
} from "../scene3d/coordinateOrigin";
import {
  markStart as section0MarkStart,
  measureSince as section0MeasureSince,
} from "./section0Instrumentation";
import {
  computeCoordinateOriginForImport,
  createCoordinateOriginAccumulator,
  currentImportHandleRef,
  finalizeStreamedViewportMembership,
  HEAL_TRANSCRIPT_COMMAND,
  hydrateCeColorFromImportedDocument,
  hydrateSysvarsFromImportedDocument,
  importMergeBumpForPriorSelection,
  INITIAL_IMPORT_PROGRESS,
  isDocumentEffectivelyEmpty,
  normalizeWorkerDocumentDefaults,
  STREAMING_IMPORT_FILE_SIZE_THRESHOLD,
  STREAMING_PROGRESS_CHUNK_INTERVAL,
  type ImportProgress,
} from "./editorStoreImportHelpers";

/**
 * The store surface this slice reads and writes — a STRUCTURAL SUBSET of
 * `EditorStore` (editorStore.ts). Member types must stay IDENTICAL to the host
 * store's declarations so `Partial<DxfImportFileHostStore>` stays assignable to
 * `Partial<EditorStore>` at the compose site (and `EditorStore` stays
 * assignable to this type for the `set`/`get` parameters). Declared here (not
 * imported from editorStore.ts) so the module has NO back-edge into
 * `editorStore.ts` — the deps graph stays acyclic. `renderTopologyVersion` is
 * additional to the slice-2 host (the streaming-viewport finalize bumps it).
 */
interface DxfImportFileHostStore {
  state: EditorShellState;
  geometryDocument: DrawingDocument;
  importProgress: ImportProgress;
  revisions: RevisionCounters;
  renderTopologyVersion: number;
  pendingBumpClasses: BumpClassSet | null;
  pendingDirectSetBumpClasses: BumpClassSet | null;
  pendingBatchBaseDoc: DrawingDocument | null;
  pendingBatchBaseState: EditorShellState | null;
  pendingBatchBaseAuditSequence: number | null;
  lastBatchClosedBy: "commit" | "abort" | "discard" | null;
}

/** The zustand `setState` of the host store, narrowed to this slice's usage. */
type DxfImportFileSet = (
  updater: (
    store: DxfImportFileHostStore,
  ) => DxfImportFileHostStore | Partial<DxfImportFileHostStore>,
) => void;

/** The zustand `getState` of the host store. */
type DxfImportFileGet = () => DxfImportFileHostStore;

/**
 * Shared editorStore-owned machinery the slice calls but does NOT own (see the
 * module JSDoc). The compose site passes the module-level
 * `rebuildEntityMetadataIndexFromImportAsync` export of `editorStore.ts`
 * verbatim.
 */
export interface DxfImportFileActionsDeps {
  /** Phase 1 (П35Р) — async chunked metadata-index rebuild. */
  rebuildEntityMetadataIndexFromImportAsync: (
    document: DrawingDocument,
    chunkSize?: number,
    options?: { forceCacheWalk?: boolean },
  ) => Promise<void>;
}

/**
 * COLLAB-LOCK-COMMAND — P2 (post-review, round 5): synthetic command stamped
 * on the audit event `importDxfFileAsync`'s fail-closed EDIT veto emits.
 * `source: "system"` marks it as an internal, non-user-initiated event —
 * mirrors `HEAL_TRANSCRIPT_COMMAND`'s convention (`editorStoreImportHelpers.ts`)
 * for the same "this import-family action has no real user `EditorCommand`
 * to attach the event to" situation. The USER-FACING summary is the
 * localised message argument to `pushAuditEvent`, not this stable marker.
 */
const IMPORT_LOCK_VETO_COMMAND: EditorCommand = {
  type: "commandline/submit",
  source: "system",
  text: "IMPORT (collab-lock veto)",
};

/**
 * The action surface this slice contributes to `EditorStore`. `editorStore.ts`
 * intersects this into its `EditorStore` type, so external importers of
 * `EditorStore` / `useEditorStore` see the exact same action signatures as
 * before the extraction.
 */
export interface DxfImportFileActionsSlice {
  importDxfFileAsync: (file: File) => Promise<DxfImportResult>;
  importDxfFileAsyncReplacing: (file: File) => Promise<DxfImportResult>;
}

/**
 * Factory for the two `File`-handle import variants. Composed into the
 * `editorStore.ts` initializer via
 * `...createDxfImportFileActionsSlice(set, get, deps)`.
 */
export function createDxfImportFileActionsSlice(
  set: DxfImportFileSet,
  get: DxfImportFileGet,
  deps: DxfImportFileActionsDeps,
): DxfImportFileActionsSlice {
  const { rebuildEntityMetadataIndexFromImportAsync } = deps;
  return {
    importDxfFileAsync: async (file) => {
      // Phase 4 Wave 2 Task 2 — route through the active document holder.
      // Phase 4 Wave 2 (codex F3 follow-up) — key by the current doc id
      // so cache writes land on the same holder the read-through wrapper
      // later reads from (`getActiveDocumentId()` used in dispatch).
      {
        const currentStoreInit = get();
        const initDocId =
          currentStoreInit.geometryDocument.id || DEFAULT_DOCUMENT_ID;
        setActiveDocument(initDocId);
      }
      // Mirror of `importDxfTextAsync` but takes a `File` handle and passes
      // it straight into the worker. The decode (codepage-aware
      // `decodeDxfBytes` over `file.arrayBuffer()`) happens
      // inside the worker thread — the main thread never materialises the
      // giant source string. Required for 100–500 MB files that would
      // otherwise OOM at decode time or during the worker→main structured
      // clone of the source.
      if (currentImportHandleRef.current) {
        currentImportHandleRef.current.abort();
        currentImportHandleRef.current = null;
      }

      set(() => ({
        importProgress: {
          phase: "decode",
          percent: 0,
          itemsProcessed: 0,
          itemsTotal: 1,
          active: true,
        },
      }));

      try {
        const currentStore = get();
        const onProgressUpdate: (
          phase: ImportPhaseName,
          itemsProcessed: number,
          itemsTotal: number,
        ) => void = (phase, itemsProcessed, itemsTotal) => {
          const percent = computeGlobalPercent(
            phase,
            itemsProcessed,
            itemsTotal,
          );
          set(() => ({
            importProgress: {
              phase,
              percent,
              itemsProcessed,
              itemsTotal,
              active: true,
            },
          }));
        };

        let importedGeometryDocument: DrawingDocument;
        // Phase 4 Wave 2 Task 4 — streaming flag: when true, the worker
        // emitted entities via `entity-chunk` messages and the result
        // document carries empty `space.entities[]` arrays. The compact
        // cache is the source of truth on this path.
        //
        // Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
        //   Gate is `file.size >= STREAMING_IMPORT_FILE_SIZE_THRESHOLD`
        //   AND worker is supported. Saves the ~1.1 GB transient
        //   worker→main structuredClone of the full DrawingDocument on
        //   685k-entity drawings.
        //
        // Codex P1 #1 (PR #87) — streaming INSERT into a NON-EMPTY seed
        // is unsafe because the chunk callback appends entities to the
        // compact cache BEFORE `mergeImportedGeometryDocumentAsync` runs.
        // The merge renames colliding IDs when the current drawing
        // already contains an entity with the same id (two drafts
        // exported from the same template, e.g.). Streaming skips
        // `clearCompactCache + populateCompactCacheFromDocumentAsync`, so
        // the cache would hold the PRE-merge ids and overwrite mappings
        // the merge resolved. Disable streaming on this path when any
        // entity is already present — fall back to the legacy single-
        // result document path which re-seeds the cache from the merged
        // doc and therefore only writes post-merge ids.
        //
        // Codex round 2 #5 (PR #87) — the inline `space.entities[]`
        // arrays are intentionally EMPTY after a streaming OPEN (the
        // worker passes `discardEntitiesAfterStreaming: true` and the
        // live entities only exist in the compact cache + metadata
        // index). Checking ONLY `s.entities.length > 0` would mis-classify
        // a fully-loaded 685k drawing as empty and allow a subsequent
        // INSERT to re-enter the streaming chunk-write path — where
        // colliding ids overwrite cache entries the merge would have
        // renamed. Gate on BOTH signals:
        //   (a) inline arrays non-empty (legacy non-streaming docs)
        //   (b) `entityMetadataIndex.size > 0` (cache-backed docs — the
        //       index is the canonical "drawing has N entities" signal)
        //   (c) `cachePopulatedOnce` sticky flag on the active holder
        //       (covers the brief window after streaming where the
        //        index rebuild is dispatched but not yet complete)
        // ANY of the three being truthy disables streaming for this
        // INSERT and falls back to the legacy single-result document
        // path that re-seeds the cache from the post-merge document.
        const activeHolder = peekDocumentMemoryHolder(
          currentStore.geometryDocument.id,
        );
        const currentDocHasEntities =
          currentStore.geometryDocument.spaces.some(
            (s) => s.entities.length > 0,
          ) ||
          entityMetadataIndex.size > 0 ||
          activeHolder?.cachePopulatedOnce === true;
        const useStreamingImport =
          isWorkerSupported() &&
          typeof file.size === "number" &&
          file.size >= STREAMING_IMPORT_FILE_SIZE_THRESHOLD &&
          !currentDocHasEntities;
        let importedViaStreaming = false;
        let streamedEntityCount = 0;
        // PR1b Phase 2b — accumulate per-chunk heal fixes the worker applied to
        // the STREAMED entities (the worker heals each entity BEFORE it is sent,
        // so the cache never sees a dangling ref). Surfaced after the stream
        // settles into the SAME bilingual transcript Phase 2a uses, folded into
        // the import baseline (undo-safe — never an undoable command).
        let streamingHealFixes: ReferenceFix[] = [];
        const streamingOriginAcc = createCoordinateOriginAccumulator();
        // GitHub Codex review (PR #534, round 3) P1 — "Track streamed entity
        // layers before import veto". The `touchedLayerNames` union further
        // below (styleTables.layers + `.spaces[].entities[].layer`) is BLIND
        // on the worker-streaming path: streamed entities are appended
        // straight into the compact cache by `onEntityChunk` and never
        // populate `importedGeometryDocument.spaces[].entities` (empty by
        // design — see the streaming-branch comment a few lines below), so a
        // large streamed INSERT whose entities reference an undeclared-but-
        // host-existing locked layer sailed straight past the veto. Collect
        // every DISTINCT `.layer` value AS EACH CHUNK ARRIVES (the only
        // point that ever sees the streamed entities) so the veto check can
        // union it in regardless of which import path populated the
        // document.
        const streamedLayerNames = new Set<string>();
        if (isWorkerSupported()) {
          // Phase 4 Wave 2 (П35Р follow-up) — chunk consumer wired only
          // when streaming is enabled for THIS file. The worker side
          // sets `stream: onEntityChunk !== undefined`, so passing
          // `undefined` here keeps small imports on the legacy single-
          // shot `result` message path (compatible with every existing
          // call site that relied on `space.entities[]` being populated
          // in the resolved document).
          let chunksSinceProgress = 0;
          let chunkConsumerError: Error | null = null;
          // Codex P1 #3 (PR #87) — track whether streaming wrote anything
          // to the compact cache so the outer catch below can roll the
          // holder back to its pre-stream state. Combined with Finding #1
          // (streaming requires an empty seed) clearing the cache on
          // failure is the correct rollback: the holder had no entries
          // before streaming started, and we drop every chunk that was
          // partially written.
          let streamingDidWriteCache = false;
          const onEntityChunk = useStreamingImport
            ? (entities: DrawingEntity[], spaceId: string): void => {
                if (chunkConsumerError !== null) return;
                try {
                  // PR1b Codex P2 FIX-3/FIX-4 — record that this space's inline
                  // entities were streaming-discarded (the cache is now its
                  // authoritative source). Marked unconditionally for every
                  // chunked space, even an empty chunk, so a space that streams
                  // then drains to empty still surfaces `[]` (not stale inline)
                  // on export.
                  markSpaceStreamed(spaceId);
                  // Codex round 2 #7 (PR #87) — flip the rollback flag
                  // BEFORE attempting the append. The flag's contract is
                  // "did streaming touch the cache at all"; a throw
                  // mid-batch may have left some entities written, so
                  // setting it post-success would leave stale data with
                  // the rollback guard masked. Setting it pre-append
                  // means the worst case is an unnecessary `clearCompactCache`
                  // when the very first chunk's append threw before
                  // writing anything (still safe — the cache was empty
                  // pre-stream per Finding #1's gate, so clearing is a
                  // no-op there).
                  if (entities.length > 0) streamingDidWriteCache = true;
                  // Batched silent append — see
                  // `appendBatchToCompactCacheSilent` JSDoc for the
                  // amortisation rationale (one holder lookup + one
                  // cacheVersion bump per chunk instead of per-entity).
                  appendBatchToCompactCacheSilent(entities);
                  // Feed the origin accumulator so the post-import
                  // `getCoordinateOrigin() === null` branch can lock in
                  // the right offset without re-walking the (now empty)
                  // inline arrays.
                  //
                  // GitHub Codex review (PR #534, round 3) P1 — feed
                  // `streamedLayerNames` in the SAME loop, for the SAME
                  // reason: this chunk callback is the only point that ever
                  // sees these entities before they are discarded into the
                  // compact cache, so the fail-closed import veto below
                  // (`touchedLayerNames`) has nowhere else to learn about a
                  // streamed entity's layer.
                  for (let i = 0; i < entities.length; i++) {
                    streamingOriginAcc.push(entities[i]!);
                    streamedLayerNames.add(entities[i]!.layer);
                  }
                  streamedEntityCount += entities.length;
                  chunksSinceProgress += 1;
                  // Throttled progress emit — see
                  // STREAMING_PROGRESS_CHUNK_INTERVAL JSDoc. We do NOT
                  // know `itemsTotal` until after the bridge has seen the
                  // full DxfDocument, so report itemsTotal as the running
                  // count too (percent computed via `computeGlobalPercent`
                  // for the bridge phase will be capped at 100). The
                  // bridge-end progress message from the worker overwrites
                  // this with the precise final count.
                  if (
                    chunksSinceProgress >= STREAMING_PROGRESS_CHUNK_INTERVAL
                  ) {
                    chunksSinceProgress = 0;
                    onProgressUpdate(
                      "bridge",
                      streamedEntityCount,
                      Math.max(streamedEntityCount, 1),
                    );
                  }
                } catch (err) {
                  // Cache append should never throw, but if it does
                  // (e.g. OOM on the silent append) capture the error
                  // and signal the worker to abort. The outer try/catch
                  // below surfaces it after the promise rejects.
                  chunkConsumerError =
                    err instanceof Error ? err : new Error(String(err));
                  currentImportHandleRef.current?.abort();
                }
              }
            : undefined;
          const handle = startDxfImportFromFileInWorker(
            file,
            (phase, _percent, itemsProcessed, itemsTotal) => {
              onProgressUpdate(phase, itemsProcessed, itemsTotal);
            },
            undefined,
            onEntityChunk,
            // PR1b Phase 2b — capture the worker's per-chunk heal fixes.
            (fixes) => {
              streamingHealFixes = fixes;
            },
          );
          currentImportHandleRef.current = handle;
          try {
            let workerDocument: DrawingDocument;
            try {
              workerDocument = await handle.promise;
            } catch (handleErr) {
              // Codex P2 #4 (PR #87) — when the chunk consumer captured
              // a real error (e.g. OOM during append) we called
              // `handle.abort()` which surfaces as `AbortError` here.
              // Surface the underlying chunk error instead so the outer
              // catch reports the actual failure rather than the user-
              // style "Import aborted" message.
              if (chunkConsumerError !== null) {
                throw chunkConsumerError;
              }
              throw handleErr;
            }
            if (chunkConsumerError !== null) {
              // Same precedence — even on a successful `handle.promise`
              // the chunk consumer may have set an error mid-stream.
              throw chunkConsumerError;
            }
            if (useStreamingImport) {
              // Streaming path: the cache was populated incrementally by
              // the chunk callback. Flip the sticky flag so the
              // read-through wrapper trusts the snapshot (final
              // `workerDocument.spaces[].entities` arrays are empty —
              // without the flag the wrapper would surface `[]`
              // everywhere). See `markCachePopulated` JSDoc.
              markCachePopulated();
              importedViaStreaming = true;
            }
            // Codex round 4 #10 (PR #87) — streamed worker documents may
            // omit optional collection fields (annotationScales, blocks,
            // blockRecords, xrefRegistry) because the chunked path
            // doesn't traverse every section. Without defensive defaults
            // here, downstream reducers (e.g. `geometryReducerAnnotative`
            // calling `annotationScales.find(...)`) crash with TypeError
            // after a streaming INSERT. Use the shared helper so the OPEN
            // and INSERT streaming branches cannot drift apart again.
            const normalizedWorker =
              normalizeWorkerDocumentDefaults(workerDocument);
            importedGeometryDocument = {
              ...normalizedWorker,
              spaces: normalizedWorker.spaces,
              id: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
            };
          } catch (innerErr) {
            // Codex P1 #3 (PR #87) — partial-stream rollback. If anything
            // failed between the first chunk and `markCachePopulated`,
            // the compact cache holds entities for a document that never
            // committed. Clear them so the read-through wrapper does not
            // surface phantom rows. Safe because Finding #1's gate
            // requires the holder to have been empty pre-stream.
            if (streamingDidWriteCache) {
              try {
                clearCompactCache();
              } catch {
                // best-effort — the outer catch still surfaces innerErr
              }
            }
            throw innerErr;
          } finally {
            if (currentImportHandleRef.current === handle) {
              currentImportHandleRef.current = null;
            }
          }
        } else {
          // jsdom / SSR fallback: decode on the main thread (only path
          // available without Worker) and run the synchronous pipeline.
          // Streaming is worker-only, so this path keeps the legacy
          // `space.entities`-populated behaviour.
          //
          // T9 IMPORT fix — codepage-aware decode (`$DWGCODEPAGE` → TextDecoder)
          // instead of `file.text()` (WHATWG UTF-8 only → mojibake on a
          // non-UTF-8 AutoCAD file). Mirrors the worker decode path.
          const source = decodeDxfBytes(await file.arrayBuffer());
          importedGeometryDocument =
            await importGeometryDocumentFromDxfTextWithProgress(source, {
              documentId: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
              onProgress: (phase, _percent, itemsProcessed, itemsTotal) => {
                onProgressUpdate(phase, itemsProcessed, itemsTotal);
              },
            });
        }

        // ── COLLAB-LOCK-COMMAND — fail-closed EDIT veto (P2, round-5) ────
        // `importDxfFileAsync` merges the imported document directly via
        // `set()` (the documented import/bootstrap exception to
        // `dispatch()`'s standard write path — ПРИОРИТЕТ #2.1), so it never
        // reaches `evaluateCollabLockVeto` through the normal `dispatch()`
        // call site. Without this check, INSERTing a DXF file containing
        // entities on a layer another collaborator holds `LOCK LAYER` on
        // would silently bypass the fail-closed guarantee this whole
        // feature exists to provide. Every entity `importedGeometryDocument`
        // carries is a BRAND-NEW id relative to THIS import (this parse just
        // minted them — they can never already be individually locked, the
        // same reasoning `dispatch()`'s veto call site documents for its own
        // `added` handling), so only the LAYER-scope check matters here:
        // every name in the imported document's OWN layer table is a layer
        // this import "touches" (either creates anew or merges into an
        // existing same-named layer down the line). Checked as EARLY as
        // possible — right after `importedGeometryDocument` becomes
        // available in EVERY branch above (worker-streaming,
        // worker-non-streaming, and this jsdom/SSR fallback) — and strictly
        // BEFORE the expensive merge/heal/populate/commit work below, so a
        // vetoed import aborts cleanly with no partial state mutation.
        //
        // Gated on a non-empty live `locks` map OR a non-"disconnected"
        // collab status — mirrors `dispatch()`'s own veto call site (P1,
        // round 5): `CollabLockClient.resetTrackedState()` clears `locks` to
        // EMPTY on every "connecting"/"error" transition too (not just a
        // genuine disconnect), so a `locks.size > 0`-only gate would SKIP
        // this check entirely during a live reconnect window. See
        // `collabLockVeto.ts`'s `authoritative` field.
        const collabState = useCollabLockStore.getState();
        if (collabState.locks.size > 0 || collabState.status !== "disconnected") {
          // GitHub Codex review (PR #534, round 2) P2 — the imported LAYER
          // TABLE alone is not a reliable proxy for every layer this import
          // actually touches: the DXF spec allows an entity to reference a
          // layer name that is NOT declared in the file's own LAYER table
          // (AutoCAD silently auto-creates a default-appearance layer record
          // for it on open). A source file missing a `Walls` table entry but
          // still carrying entities with `layer: "Walls"` used to slip past
          // this check entirely — those entities land on the HOST document's
          // EXISTING "Walls" layer once merged below, even if another
          // collaborator holds `LOCK LAYER Walls` right now. Union the
          // layer-table names with every DISTINCT `.layer` value the
          // imported entities themselves carry (same
          // `spaces.flatMap((s) => s.entities)` walk used elsewhere in this
          // file to enumerate the full imported entity set) so both the
          // declared AND the merely-referenced layers are covered.
          //
          // GitHub Codex review (PR #534, round 3) P1 — on the WORKER
          // STREAMING path `importedGeometryDocument.spaces[].entities` is
          // ALWAYS empty (streamed entities never populate the inline
          // arrays — see `markSpaceStreamed`), so the entity-layer walk
          // above sees nothing there; fold in `streamedLayerNames` (fed by
          // `onEntityChunk` above, empty on every non-streaming path) so a
          // streamed INSERT's entity-referenced-but-undeclared layers are
          // covered too.
          const touchedLayerNames = [
            ...new Set([
              ...(importedGeometryDocument.styleTables?.layers ?? []).map((layer) => layer.name),
              ...importedGeometryDocument.spaces.flatMap((s) => s.entities).map((e) => e.layer),
              ...streamedLayerNames,
            ]),
          ];
          const importVeto = evaluateCollabLockVeto({
            touchedEntities: [],
            touchedLayerNames,
            locks: collabState.locks,
            selfParticipantId: collabState.selfParticipantId,
            authoritative: collabState.status === "connected",
          });
          if (importVeto) {
            const message =
              importVeto.kind === "unavailable"
                ? t("collab.lock.edit_rejected_unavailable")
                : t("collab.lock.edit_rejected", {
                    target: importVeto.scope.targetId,
                    owner: importVeto.heldByName,
                  });
            // P2 (post-review, round 6, finding #14) — on the WORKER
            // STREAMING path, entity chunks were already appended to the
            // compact cache and `markCachePopulated()` already ran (both
            // happen well before this veto check, inside the try block
            // above) — so "no state past this point has mutated" is true
            // for the merge/heal/populate/commit phase BELOW this check,
            // but NOT for the cache the streaming chunk consumer already
            // wrote. The generic outer `catch` a few hundred lines down
            // only resets `importProgress`; it does not know this throw
            // followed a completed stream, so without this rollback a
            // vetoed streaming INSERT would leave the imported entities in
            // the active document holder as phantom data for
            // read-through/export paths even though the import result
            // reports failure. Mirrors the inner stream-failure catch's
            // OWN rollback a few lines above (`streamingDidWriteCache` →
            // `clearCompactCache()`) — safe for the identical reason
            // documented there: streaming requires an empty pre-stream
            // seed, so clearing the holder here returns it to EXACTLY its
            // pre-import state.
            if (importedViaStreaming) {
              try {
                clearCompactCache();
              } catch {
                // best-effort — the throw below still surfaces the veto message
              }
            }
            set((store) => ({
              state: pushAuditEvent(
                store.state,
                IMPORT_LOCK_VETO_COMMAND,
                message,
                "error",
              ),
            }));
            // Thrown (rather than returned directly) so the SAME catch
            // block below — which already resets `importProgress` and
            // shapes the `{ ok: false, message }` result — handles this
            // exactly like every other mid-import failure. No DOCUMENT
            // state past this point has mutated yet (the merge/heal/
            // populate/commit phase starts below) — and the streamed
            // CACHE writes, the one piece of state that already ran ahead
            // of this check, were just rolled back above — so this is now
            // a clean, atomic abort either way (streaming or not).
            throw new Error(message);
          }
        }

        // ── PR-A: coordinate-offset compute (first import only) ──────
        // Locks in the origin so the Three.js renderer keeps Float32
        // precision on geodetic-scale surveys. Subsequent imports
        // re-use the same offset (Q27.A — keep-first). Failure to
        // compute (no entities, NaN bbox) is silently swallowed —
        // adapters then run as identity.
        if (getCoordinateOrigin() === null) {
          // Phase 4 Wave 2 Task 4 — on the streaming path the inline
          // `space.entities[]` arrays are empty; the bbox accumulator
          // built by the chunk callback is the only source. Fall back
          // to the inline array variant otherwise.
          const origin = importedViaStreaming
            ? streamingOriginAcc.finalize()
            : computeCoordinateOriginForImport(
                importedGeometryDocument.spaces.flatMap((s) => s.entities),
              );
          if (origin !== null) {
            try {
              setCoordinateOrigin(origin);
            } catch {
              // setter throws on second call — race-safe no-op.
            }
          }
        }

        // ── Phase: merge ──────────────────────────────────────────────
        set((store) => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
          state: store.state,
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        // Phase 1.c (П35Р) — skip merge entirely when the current
        // document has no entities (fresh seed). For 685k-entity OPEN
        // into an empty seed the merge is wasted work — it copies every
        // imported entity into a fresh target Map and rebuilds the
        // document, doubling peak memory and adding ~3.4s of sync work
        // to the critical path. Using imported doc directly preserves
        // all merge outputs since the seed has nothing to contribute
        // except schema defaults (which the bridge already populated).
        // Codex round 4 #9 (PR #87) — after streaming OPEN the inline
        // `space.entities[]` arrays are empty by design (live entities
        // exist only in the compact cache + metadata index). Checking
        // ONLY inline arrays here would mis-classify a fully-loaded
        // 685k-entity drawing as "empty seed" and let a subsequent
        // INSERT REPLACE the doc with the inserted file (silent data
        // loss). `isDocumentEffectivelyEmpty` mirrors the round-2
        // `currentDocHasEntities` gate at line ~2812 — inline arrays
        // + `entityMetadataIndex.size` + `cachePopulatedOnce` sticky
        // flag; any truthy signal forces the full merge path.
        const currentIsEmptySeed = isDocumentEffectivelyEmpty(
          currentStore.geometryDocument,
        );
        // GitHub Codex review PR #582 finding 2 — replacement-import twin
        // of the two `editorStoreImportActions.ts` text paths: the
        // empty-seed bypass below REPLACES the document while REUSING its
        // id (`id: currentStore.geometryDocument.id`, also stamped on the
        // streaming branch above), so the per-entity provenance ledger
        // keyed by that id must not survive into the new document. Placed
        // OUTSIDE the `!importedViaStreaming` cache-reseed guard further
        // down: the streaming branch reuses the id too and never reaches
        // that guard. `importDxfFileAsyncReplacing` (the file-OPEN entry
        // point, further below in this same file) is already covered on
        // its own terms — it mints a NEW document id and, once the swap
        // is confirmed, calls `dropDocumentProvenance(previousDocId)`
        // (paired with an earlier `dropDocumentHolder(previousDocId)` for
        // memory headroom — see the `EntityProvenance` ownership-rule doc
        // comment in `mutationStore.ts` for why that path splits the
        // teardown across the async parse instead of calling
        // `clearDocument` synchronously the way this INSERT path does).
        if (currentIsEmptySeed) {
          clearEntityProvenance(
            currentStore.geometryDocument.id || DEFAULT_DOCUMENT_ID,
          );
        }
        const mergedRawDocument: DrawingDocument = currentIsEmptySeed
          ? {
              ...importedGeometryDocument,
              id: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
              savedView: currentStore.geometryDocument.savedView,
            }
          : await mergeImportedGeometryDocumentAsync(
              currentStore.geometryDocument,
              importedGeometryDocument,
              // Codex #343 round-1 FINDING 1 — reached ONLY on a true INSERT (the
              // empty-seed OPEN takes the `{...imported}` bypass above with the
              // imported $CECOLOR whole), so PRESERVE the non-empty host $CECOLOR.
              undefined,
              false,
            );
        // PR1b Phase 2a — heal dangling refs on the WHOLE document before
        // populate + commit (folded into the import baseline → undo-safe;
        // plan §12 / Codex#2 P2-5). Only the NON-streaming branch has the full
        // `space.entities[]` in hand here; on the streaming branch the entities
        // live only in the compact cache.
        // PR1b Phase 2b (plan §12 option (a)) — the streaming branch heals each
        // entity IN THE WORKER (per-chunk, before discard); `streamingHealFixes`
        // carries the applied substitutions and its summary is folded into the
        // baseline state below (same undo-safe contract as the whole-doc path).
        const importHeal = importedViaStreaming
          ? null
          : healImportedDocument(mergedRawDocument);
        const mergedGeometryDocument = importHeal
          ? importHeal.document
          : mergedRawDocument;
        set(() => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 1, 1),
            itemsProcessed: 1,
            itemsTotal: 1,
            active: true,
          },
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // Phase 4 Wave 2 (П35Р follow-up) — bulk populate the compact
        // cache from the merged document on the non-streaming branch.
        // Kept symmetric with `importDxfTextAsync` (codex F2 follow-up):
        // without this the read-through wrapper would start returning
        // partial subsets after the first mutation.
        //
        // Phase 1 (П35Р) — use async chunked populate so the main thread
        // yields to the event loop every ~2000 entities. The sync
        // version blocks for 3-5s on 685k entities; Playwright probes
        // and Three.js animation frames cannot fire during that window,
        // causing the tab to appear hung.
        if (!importedViaStreaming) {
          clearCompactCache();
          await populateCompactCacheFromDocumentAsync(mergedGeometryDocument);
        }

        // ── Phase: sync ───────────────────────────────────────────────
        set((store) => ({
          importProgress: {
            phase: "sync",
            percent: computeGlobalPercent("sync", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
          state: store.state,
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        // Section 0 — final geometryDocument + shell-state commit (file
        // path). post-V9 Section 0 Iter 6.D — slim-state-as-pointer
        // (Phase 3b PR #6) keeps `geometryDocument.spaces[i].entities`
        // populated for the huge path; non-reactive duplication lives
        // in the compact cache (id-indexed columnar store).
        const tSetFile = section0MarkStart("client:setState");
        set((store) => {
          let nextState = syncShellStateFromMergedGeometryDocument(
            store.state,
            mergedGeometryDocument,
            { assumeValid: true },
          );
          // PR1b Phase 2a/2b — fold the heal summary into the import-baseline
          // state. Non-streaming: whole-doc `importHeal`. Streaming: the worker's
          // per-chunk `streamingHealFixes` (Phase 2b). Folding into the baseline
          // (not via commitMutation) keeps heal out of the undo stack — the first
          // history state already reflects healed refs (undo-safe; Codex#2 P2-5).
          const streamHeal = importedViaStreaming
            ? buildStreamingHealResult(streamingHealFixes)
            : null;
          const foldSummary =
            importHeal?.summary ?? streamHeal?.summary ?? null;
          const foldTone = importHeal?.tone ?? streamHeal?.tone ?? "info";
          if (foldSummary) {
            nextState = pushAuditEvent(
              nextState,
              HEAL_TRANSCRIPT_COMMAND,
              foldSummary,
              foldTone,
            );
          }
          return {
            state: {
              ...nextState,
              // Phase 4.1.j — clear history on import (same rationale as
              // sync importDxfText path above).
              history: {
                past: [],
                future: [],
                current: null,
                depthCap: store.state.history.depthCap,
                droppedCount: 0,
              },
            },
            geometryDocument: mergedGeometryDocument,
            // ABB-S1 G3 #5 — INSERT-style MERGE (see the sync path): DUAL-BUMP
            // geometry + table (+ snapshot once), NO epoch mint. History flushed →
            // clear any in-flight batch accumulator (both — round-3 F2).
            // PR #362 round-5 (F2) — selection bumps ONLY if the merge actually
            // changes it: it clears the pickset unconditionally, so a real change
            // happens iff the PRIOR selection was non-empty (empty→empty = no-op).
            revisions: bumpForClasses(
              store.revisions,
              importMergeBumpForPriorSelection(store.state.selection.entityIds),
            ),
            pendingBumpClasses: null,
            pendingDirectSetBumpClasses: null,
            // round-4 GENERALIZATION 2 — a merge flushes history; drop the base.
            pendingBatchBaseDoc: null,
            pendingBatchBaseState: null,
            pendingBatchBaseAuditSequence: null,
            // Gate #4 round-2 (FIX C) — this import/open close clears
            // `history.current` WITHOUT a commit: staged telemetry is DISCARDED.
            lastBatchClosedBy: "discard",
            importProgress: {
              phase: "sync",
              percent: 100,
              itemsProcessed: 1,
              itemsTotal: 1,
              active: true,
            },
          };
        });
        section0MeasureSince("client:setState", tSetFile);
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // PR-V1 — populate the EntityMetadataIndex. post-V9 Section 0
        // Iter 6.D: slim-state-as-pointer (Phase 3b PR #6) keeps
        // `space.entities` populated for huge imports too, so the
        // metadata-index walk reads from `space.entities` directly.
        // Phase 4 Wave 2 Task 4: when streaming, the inline arrays are
        // empty — `rebuildEntityMetadataIndexFromImport` automatically
        // falls back to `snapshotSpaceEntities` for those spaces.
        // Phase 1 (П35Р) — async chunked walker.
        // Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
        //   INSERT into a non-empty seed produces a `mergedGeometryDocument`
        //   whose inline `space.entities[]` arrays hold ONLY the prior
        //   document's entities (because the imported doc's arrays are
        //   empty when streamed). The cache, however, holds the union —
        //   force the metadata walker through the cache walker so the
        //   index sees every streamed entity.
        await rebuildEntityMetadataIndexFromImportAsync(
          mergedGeometryDocument,
          undefined,
          { forceCacheWalk: importedViaStreaming },
        );
        // PR2 (plan §13) — build the dependency index from the merged document.
        // Streamed spaces (whose inline arrays are empty) are rematerialised from
        // the compact store inside the helper, keyed off `holder.streamedSpaceIds`.
        populateDependencyIndexFromDocument(mergedGeometryDocument);

        // EPIC-VIEWPORT-CLIP SLICE 3 — streaming-path membership finalize (same
        // rationale as the OPEN path): the worker discarded model-space entities,
        // so recompute `childEntityIds` on the main thread from the cache. clipPath
        // was already resolved in the worker. Bump renderTopologyVersion so the
        // viewport child pass re-reads.
        if (importedViaStreaming) {
          const populated = finalizeStreamedViewportMembership(
            mergedGeometryDocument,
            mergedGeometryDocument.id,
          );
          if (populated) {
            set((store) => ({
              renderTopologyVersion: store.renderTopologyVersion + 1,
            }));
          }
        }
        // Codex #342 round-6 P2 (F2): INSERT/merge PRESERVES the live sysvar store
        // (the in-progress session owns MIRRTEXT/OSMODE/DYN*; export projects them
        // out via `applyLiveHeaderSysvars`). ONLY hydrate FROM the imported header
        // when the prior doc was an empty seed (= a fresh OPEN-equivalent) — reusing
        // the `currentIsEmptySeed` flag captured above the merge. An INSERT into a
        // live drawing with a SETVAR-changed $MIRRTEXT/$OSMODE no longer stomps it;
        // the OPEN path (`importDxfFileAsyncReplacing` + App.tsx) still hydrates
        // unconditionally because it replaces the document.
        if (currentIsEmptySeed) {
          hydrateSysvarsFromImportedDocument(mergedGeometryDocument);
          hydrateCeColorFromImportedDocument(mergedGeometryDocument);
        }

        // post-V9 Section 0 Iter 6.D — `space.entities.length` is
        // authoritative across both cap-busting and below-cap imports
        // thanks to slim-state-as-pointer (Phase 3b PR #6).
        // Phase 4 Wave 2 Task 4 — on the streaming path the inline
        // arrays are empty; use the streaming counter we tallied in
        // the chunk callback so the message text is accurate.
        const importedEntityCount = importedViaStreaming
          ? streamedEntityCount
          : importedGeometryDocument.spaces.reduce(
              (count, space) => count + space.entities.length,
              0,
            );

        await new Promise<void>((resolve) => setTimeout(resolve, 120));
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));

        return {
          ok: true,
          message: `Imported ${importedEntityCount} supported entit${importedEntityCount === 1 ? "y" : "ies"} into the current drawing across ${importedGeometryDocument.spaces.length} space${importedGeometryDocument.spaces.length === 1 ? "" : "s"}.`,
          entityCount: importedEntityCount,
          spaceCount: importedGeometryDocument.spaces.length,
        };
      } catch (error) {
        if (currentImportHandleRef.current) {
          currentImportHandleRef.current = null;
        }
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));
        const isAbort = error instanceof Error && error.name === "AbortError";
        return {
          ok: false,
          message: isAbort
            ? t("dxfio.status.aborted")
            : error instanceof Error
              ? error.message
              : String(error),
        };
      }
    },
    importDxfFileAsyncReplacing: async (file) => {
      // Phase 4 Wave 2 Task 2 — OPEN flow: a fresh doc id is generated
      // below before we touch the compact cache. We pre-create the
      // holder under the new id so cache writes + the read-through
      // wrapper agree (codex F3 follow-up — single-doc was fine before
      // because both routed to DEFAULT, but multi-doc futures need the
      // holder keyed by the real doc id from the outset).
      // AutoCAD `OPEN`-style import. Mirrors the structure of
      // `importDxfFileAsync` but **replaces** the current document
      // instead of merging. Document-scoped state (selection,
      // MATCHPROP source, entity caches, metadata index) is reset; the
      // session/UI state on the rest of the store is preserved.
      //
      // Rationale: every prior import path went through
      // `mergeImportedGeometryDocument`, which APPENDED entities and
      // produced surprising behaviour for users who expected a file
      // picker to "open" a new drawing. AutoCAD distinguishes OPEN
      // (replace) and INSERT (merge); this action covers the OPEN
      // semantics. The legacy `importDxfFileAsync` remains in place for
      // a future INSERT UI affordance.
      if (currentImportHandleRef.current) {
        currentImportHandleRef.current.abort();
        currentImportHandleRef.current = null;
      }

      set(() => ({
        importProgress: {
          phase: "decode",
          percent: 0,
          itemsProcessed: 0,
          itemsTotal: 1,
          active: true,
        },
      }));

      // PR #582 live Codex finding ("Delay provenance teardown until OPEN
      // commits") — hoisted OUTSIDE the try so the `catch` below can see it
      // too. Assigned once the outgoing document's id is known (inside the
      // try); on a failed/aborted parse the `catch` restores
      // `activeDocumentId` to this value so every module-state read
      // (provenance ledger, compact-cache holder) routes back to the
      // document that's still the one visible in the UI — the swap to the
      // new document only ever lands on the success path below.
      let previousDocId: string | null = null;
      // PR #582 follow-up Codex finding ("Do not restore routing after the
      // OPEN swap") — the TRUE document SWAP `set(...)` below is not the
      // LAST thing this `try` does: metadata-index rebuild, dependency-
      // index population and streamed-viewport finalization all run AFTER
      // it, still inside the same `try`. If one of THOSE throws, the swap
      // already committed — `geometryDocument` in the store IS the new
      // document — so the `catch` must NOT repoint `activeDocumentId` back
      // at the outgoing document (whose holder was already dropped); doing
      // so would route every subsequent provenance/compact-cache read to a
      // document the UI no longer shows. Flipped to `true` at the swap
      // point, right where `dropDocumentProvenance` runs.
      let openSwapCommitted = false;
      try {
        const currentStore = get();
        const onProgressUpdate: (
          phase: ImportPhaseName,
          itemsProcessed: number,
          itemsTotal: number,
        ) => void = (phase, itemsProcessed, itemsTotal) => {
          const percent = computeGlobalPercent(
            phase,
            itemsProcessed,
            itemsTotal,
          );
          set(() => ({
            importProgress: {
              phase,
              percent,
              itemsProcessed,
              itemsTotal,
              active: true,
            },
          }));
        };

        let importedGeometryDocument: DrawingDocument;
        // Phase 4 Wave 2 Task 4 — streaming flag for the OPEN path
        // (mirrors importDxfFileAsync). When true, the result document
        // carries empty `space.entities[]`; the compact cache is the
        // source of truth.
        //
        // Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
        //   Same gate as `importDxfFileAsync`. The OPEN path benefits
        //   even more from streaming because the previous doc's holder
        //   is dropped (`dropDocumentHolder(previousDocId)`) right before
        //   the worker call — there is no "preserve previous entities"
        //   wrinkle to worry about.
        const useStreamingImport =
          isWorkerSupported() &&
          typeof file.size === "number" &&
          file.size >= STREAMING_IMPORT_FILE_SIZE_THRESHOLD;
        let importedViaStreaming = false;
        let streamedEntityCount = 0;
        // PR1b Phase 2a — heal result for the OPEN (replace) path. Populated on
        // the NON-streaming branches (worker-legacy + jsdom fallback) where the
        // full `space.entities[]` is in hand BEFORE populate; the healed doc is
        // what gets populated AND committed, so cache/store never diverge and the
        // first history baseline is already healed (undo-safe; Codex#2 P2-5).
        // PR1b Phase 2b — the streaming branch heals each entity IN THE WORKER
        // (per-chunk, before discard); `streamingHealFixes` carries the applied
        // substitutions and its summary is folded into the OPEN baseline below.
        let importHeal: ImportHealResult | null = null;
        let streamingHealFixes: ReferenceFix[] = [];
        const streamingOriginAcc = createCoordinateOriginAccumulator();
        if (isWorkerSupported()) {
          // Phase 4 Wave 2 Task 4 — for streaming, the chunk callback
          // fires WHILE the worker is parsing — i.e. before
          // `await handle.promise` returns. We must therefore pin the
          // active doc id and clear the cache BEFORE invoking the
          // worker, otherwise the appended entities would land in the
          // OLD doc's holder.
          const workerGeneratedId =
            typeof globalThis.crypto?.randomUUID === "function"
              ? globalThis.crypto.randomUUID()
              : `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          const workerTitleFromFile = file.name.replace(/\.dxf$/i, "").trim();
          // Phase 4 Wave 2 (codex F3 follow-up) — drop the previous
          // doc's holder before flipping active. PR #582 live finding —
          // only the (memory-heavy) holder, NOT the provenance ledger:
          // the parse below can still fail/abort, at which point this
          // document is still the one visible in the UI (see the
          // `previousDocId` doc comment above the `try`).
          previousDocId = currentStore.geometryDocument.id;
          if (previousDocId && previousDocId !== workerGeneratedId) {
            dropDocumentHolder(previousDocId);
          }
          setActiveDocument(workerGeneratedId);
          // Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
          //   Pre-clear the (newly-active, empty) holder's cache so the
          //   streaming chunks are the only contributors. Cheap on an
          //   empty holder; mandatory if a prior import / dispatch left
          //   residue under this id (defensive — dropDocumentHolder above
          //   already wipes the previous id's holder, but the new id is
          //   fresh in normal flow).
          if (useStreamingImport) {
            clearCompactCache();
          }
          let chunksSinceProgress = 0;
          let chunkConsumerError: Error | null = null;
          // Codex P1 #3 (PR #87) — track partial-stream writes for the
          // OPEN-path rollback. OPEN already pre-cleared the cache above,
          // so on failure we restore the same empty state by clearing
          // again.
          let streamingDidWriteCache = false;
          const onEntityChunk = useStreamingImport
            ? (entities: DrawingEntity[], spaceId: string): void => {
                if (chunkConsumerError !== null) return;
                try {
                  // PR1b Codex P2 FIX-3/FIX-4 — record streaming provenance for
                  // this space (see INSERT-path twin). The cache is now the
                  // authoritative source; export must prefer it over the
                  // retained inline shell (e.g. paper-space Viewport).
                  markSpaceStreamed(spaceId);
                  // Codex round 2 #7 (PR #87) — flip the rollback flag
                  // BEFORE the append (see INSERT-path twin). A mid-
                  // batch throw may have written some entities; the
                  // flag's purpose is "did streaming touch the cache"
                  // and setting it post-success would skip clearing
                  // partial writes.
                  if (entities.length > 0) streamingDidWriteCache = true;
                  appendBatchToCompactCacheSilent(entities);
                  for (let i = 0; i < entities.length; i++) {
                    streamingOriginAcc.push(entities[i]!);
                  }
                  streamedEntityCount += entities.length;
                  chunksSinceProgress += 1;
                  if (
                    chunksSinceProgress >= STREAMING_PROGRESS_CHUNK_INTERVAL
                  ) {
                    chunksSinceProgress = 0;
                    onProgressUpdate(
                      "bridge",
                      streamedEntityCount,
                      Math.max(streamedEntityCount, 1),
                    );
                  }
                } catch (err) {
                  chunkConsumerError =
                    err instanceof Error ? err : new Error(String(err));
                  currentImportHandleRef.current?.abort();
                }
              }
            : undefined;
          const handle = startDxfImportFromFileInWorker(
            file,
            (phase, _percent, itemsProcessed, itemsTotal) => {
              onProgressUpdate(phase, itemsProcessed, itemsTotal);
            },
            undefined,
            onEntityChunk,
            // PR1b Phase 2b — capture the worker's per-chunk heal fixes.
            (fixes) => {
              streamingHealFixes = fixes;
            },
          );
          currentImportHandleRef.current = handle;
          try {
            let workerDocument: DrawingDocument;
            try {
              workerDocument = await handle.promise;
            } catch (handleErr) {
              // Codex P2 #4 (PR #87) — prefer the chunk-consumer error
              // over the abort surfaced by `handle.abort()`. See the
              // INSERT path for the rationale.
              if (chunkConsumerError !== null) {
                throw chunkConsumerError;
              }
              throw handleErr;
            }
            if (chunkConsumerError !== null) {
              throw chunkConsumerError;
            }
            // OPEN semantics: stamp the document with metadata DERIVED FROM
            // THE PICKED FILE, not whatever default the worker parser
            // produced. The worker calls `importDxfDocumentToDrawingDocument`
            // without options, which defaults id to "dxf-import" and title
            // to "Imported DXF" — so opening different DXF files would all
            // produce the same identity and export filename (codex P2 on
            // PR #58, commit 559f8e1). Match the non-worker fallback
            // pattern below so behaviour is identical in both environments.
            const tCache = section0MarkStart("client:cachePopulate");
            const tCompact = section0MarkStart("client:compactCachePopulate");
            if (useStreamingImport) {
              // Streaming path: cache already populated by the chunk
              // callback (entities discarded from `workerDocument.spaces`
              // via `discardEntitiesAfterStreaming: true`). Flip the
              // sticky `cachePopulatedOnce` flag so the read-through
              // wrapper surfaces the streamed entities.
              // PR1b Phase 2b (plan §12 option (a)) — the streamed entities were
              // healed PER-CHUNK in the worker BEFORE discard (see the worker's
              // `onEntityImported` hook + `buildHealResolutionFromDxf`), so the
              // cache already holds healed refs. `streamingHealFixes` carries the
              // applied substitutions; its summary is folded into the OPEN
              // baseline below (undo-safe — never an undoable command).
              markCachePopulated();
              importedViaStreaming = true;
            } else {
              // Legacy bulk-populate path — wipes any residue from the
              // (freshly active) holder so the resolved document is the
              // single source of truth. Phase 1 (П35Р) async chunked.
              // PR1b Phase 2a — heal dangling refs on the WHOLE document
              // BEFORE populate so the cache holds healed values (the read-
              // through wrapper is the render source of truth on this path).
              importHeal = healImportedDocument(workerDocument);
              clearCompactCache();
              await populateCompactCacheFromDocumentAsync(importHeal.document);
            }
            section0MeasureSince("client:compactCachePopulate", tCompact);
            section0MeasureSince("client:cachePopulate", tCache);
            // Use the healed document on the non-streaming branch; the streaming
            // branch (importHeal === null) keeps the raw worker document — its
            // entities live only in the cache and are healed in Phase 2b.
            const baseWorkerDocument = importHeal
              ? importHeal.document
              : workerDocument;
            importedGeometryDocument = {
              ...baseWorkerDocument,
              id: workerGeneratedId,
              title:
                workerTitleFromFile.length > 0
                  ? workerTitleFromFile
                  : "Imported drawing",
              spaces: baseWorkerDocument.spaces,
            };
          } catch (innerErr) {
            // Codex P1 #3 (PR #87) — partial-stream rollback for the OPEN
            // path. The cache was pre-cleared above; clearing again puts
            // the holder back to the empty state it had before streaming.
            if (streamingDidWriteCache) {
              try {
                clearCompactCache();
              } catch {
                // best-effort; the outer catch still surfaces innerErr
              }
            }
            throw innerErr;
          } finally {
            if (currentImportHandleRef.current === handle) {
              currentImportHandleRef.current = null;
            }
          }
        } else {
          // T9 IMPORT fix — codepage-aware decode (`$DWGCODEPAGE` → TextDecoder)
          // instead of `file.text()` (WHATWG UTF-8 only → mojibake on a
          // non-UTF-8 AutoCAD file). Mirrors the worker decode path.
          const source = decodeDxfBytes(await file.arrayBuffer());
          // OPEN fallback (no Worker / jsdom / SSR): generate fresh
          // document identity from the file rather than inheriting from
          // the current drawing. The Worker path obtains a fresh id from
          // the worker's parser; this branch must mirror that or codex
          // P2 reports "non-worker fallback silently keeps previous
          // drawing metadata" (PR #58, commit 66e2f65).
          const generatedId =
            typeof globalThis.crypto?.randomUUID === "function"
              ? globalThis.crypto.randomUUID()
              : `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          const titleFromFile = file.name.replace(/\.dxf$/i, "").trim();
          // Phase 4 Wave 2 (codex F3 follow-up) — drop the previous
          // doc's holder + route cache writes through the new doc id.
          // PR #582 live finding — only the holder, not the provenance
          // ledger; see the `previousDocId` doc comment above the `try`.
          previousDocId = currentStore.geometryDocument.id;
          if (previousDocId && previousDocId !== generatedId) {
            dropDocumentHolder(previousDocId);
          }
          setActiveDocument(generatedId);
          const importedRawDocument =
            await importGeometryDocumentFromDxfTextWithProgress(source, {
              documentId: generatedId,
              title:
                titleFromFile.length > 0 ? titleFromFile : "Imported drawing",
              onProgress: (phase, _percent, itemsProcessed, itemsTotal) => {
                onProgressUpdate(phase, itemsProcessed, itemsTotal);
              },
            });
          // PR1b Phase 2a — heal dangling refs before populate so cache +
          // store agree (the jsdom fallback is never streaming).
          importHeal = healImportedDocument(importedRawDocument);
          importedGeometryDocument = importHeal.document;
          // jsdom / SSR fallback: still wipe the compact cache so the
          // reactive store does not race with stale entries. Phase 4
          // Wave 2 (codex F2 follow-up) — eager populate flips the
          // populated flag so the read-through wrapper trusts snapshots.
          // Phase 1 (П35Р) — async chunked populate.
          clearCompactCache();
          await populateCompactCacheFromDocumentAsync(importedGeometryDocument);
        }

        // PR-A: coordinate-offset compute (first import only). Same
        // contract as the merge path — if no origin is set yet, lock one
        // in. Subsequent OPENs reuse it (Q27.A — keep-first) so Float32
        // precision stays consistent for the user's session.
        if (getCoordinateOrigin() === null) {
          // Phase 4 Wave 2 Task 4 — streaming path uses the bbox
          // accumulator built by the chunk callback; non-streaming
          // path keeps the legacy inline-array walk.
          const origin = importedViaStreaming
            ? streamingOriginAcc.finalize()
            : computeCoordinateOriginForImport(
                importedGeometryDocument.spaces.flatMap((s) => s.entities),
              );
          if (origin !== null) {
            try {
              setCoordinateOrigin(origin);
            } catch {
              // setter throws on second call — race-safe no-op.
            }
          }
        }

        // Phase: merge — for OPEN semantics we don't merge with the
        // CURRENT document (that would APPEND entities). Instead we
        // merge with a fresh empty seed so all schema defaults
        // (annotationScales: [], anno* flags, dimensionStyles, layers,
        // blocks etc.) populate correctly. This keeps downstream consumers
        // — that rely on `geometryDocument.annotationScales.length` and
        // similar invariants — from crashing when the imported file has
        // no scales / no styles / etc.
        set((store) => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
          state: store.state,
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        // Phase 1.c (П35Р) — OPEN merges against a fresh empty seed.
        // The merge has nothing to contribute except schema defaults
        // (which the bridge already filled). Skip the merge entirely
        // and use the imported doc directly. Saves ~3.4s sync work on
        // 685k-entity drawings and avoids the ~500 MB intermediate
        // `spacesById` Map of entity copies.
        // Codex round 4 #10 (PR #87) — share defensive-default logic
        // with the INSERT streaming branch via `normalizeWorkerDocumentDefaults`.
        // Prevents the two paths from drifting apart (the round-3 fix
        // had inline defaults only on OPEN, leaving INSERT crash-prone).
        const mergedGeometryDocument: DrawingDocument = {
          ...normalizeWorkerDocumentDefaults(importedGeometryDocument),
          id: importedGeometryDocument.id,
          title: importedGeometryDocument.title,
          activeSpaceId: importedGeometryDocument.activeSpaceId,
          savedView: importedGeometryDocument.savedView,
        };
        set(() => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 1, 1),
            itemsProcessed: 1,
            itemsTotal: 1,
            active: true,
          },
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // Phase: sync — assign the freshly-parsed document directly and
        // rebuild the shell state from it. `syncShellStateFromMergedGeometryDocument`
        // already wipes selection + MATCHPROP source as part of its
        // post-condition (see Phase 3b PR #6 note in that helper), which
        // is exactly the OPEN behaviour we want.
        set(() => ({
          importProgress: {
            phase: "sync",
            percent: computeGlobalPercent("sync", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        const tSetFile = section0MarkStart("client:setState");
        // COLLAB-LOCK-COMMAND (post-review, round 4) — this branch is the
        // TRUE document SWAP (see the `mintNewDocumentEpoch` ABB-S1 note
        // below) — drop any live collab-lock connection BEFORE the swap
        // lands, so the fail-closed veto below never operates against a
        // stale companion room keyed by the DOCUMENT WE ARE LEAVING.
        useCollabLockStore.getState().disconnect();
        // Phase 1.k final — drop startTransition wrap. Concurrent retries
        // of a transition render can themselves accumulate toward React's
        // 50-update budget, especially when the bulk-load `invalidate()`
        // chain keeps preempting the transition with higher-priority
        // updates. Synchronous set keeps the import deterministic.
        set((store) => {
          let nextState = syncShellStateFromMergedGeometryDocument(
            store.state,
            mergedGeometryDocument,
            { assumeValid: true },
          );
          // PR1b Phase 2a/2b — fold the heal summary into the OPEN-baseline
          // state. Non-streaming: whole-doc `importHeal`. Streaming: the worker's
          // per-chunk `streamingHealFixes` (Phase 2b). Folding into the baseline
          // (not via commitMutation) keeps heal out of the undo stack.
          const streamHeal = importedViaStreaming
            ? buildStreamingHealResult(streamingHealFixes)
            : null;
          const foldSummary =
            importHeal?.summary ?? streamHeal?.summary ?? null;
          const foldTone = importHeal?.tone ?? streamHeal?.tone ?? "info";
          if (foldSummary) {
            nextState = pushAuditEvent(
              nextState,
              HEAL_TRANSCRIPT_COMMAND,
              foldSummary,
              foldTone,
            );
          }
          return {
            state: {
              ...nextState,
              // Gate #4 round-3 (FIX #1 — editorStore.ts:6268) — a TRUE document
              // SWAP (OPEN a different file in this tab) flushes history. The other
              // import branches (4863/5165/5712) already rebuild `history` to a
              // cleared stack here; this branch returned `state: nextState` BARE,
              // and `syncShellStateFromMergedGeometryDocument` PRESERVES the
              // incoming `state.history` (it spreads `...state`) — so opening a file
              // WHILE a batch was open left `history.current != null`. The telemetry
              // subscriber then stayed in stage-while-open mode, never consumed the
              // "discard" signal, kept the staged events buffered, AND suppressed
              // every later event (their `history.current != null` check). Clearing
              // the stack here (matching the sibling import branches) makes the
              // close unambiguous: `current: null` + `lastBatchClosedBy: "discard"`.
              history: {
                past: [],
                future: [],
                current: null,
                depthCap: store.state.history.depthCap,
                droppedCount: 0,
              },
            },
            geometryDocument: mergedGeometryDocument,
            // ABB-S1 — TRUE document SWAP (OPEN a different file in this tab; the
            // worker `5054` + fallback `5225` paths both minted a fresh
            // geometryDocument.id and converge here). Reset all five counters and
            // MINT a new documentEpoch. NOT done at the INSERT/import re-stamp
            // returns above (4183/4464/4992) — those keep the same id verbatim.
            revisions: mintNewDocumentEpoch(store.revisions),
            pendingBumpClasses: null,
            pendingDirectSetBumpClasses: null,
            // round-4 GENERALIZATION 2 — a document SWAP flushes history; drop base.
            pendingBatchBaseDoc: null,
            pendingBatchBaseState: null,
            pendingBatchBaseAuditSequence: null,
            // Gate #4 round-2 (FIX C) — this import/open close clears
            // `history.current` WITHOUT a commit: staged telemetry is DISCARDED.
            lastBatchClosedBy: "discard",
            importProgress: {
              phase: "sync",
              percent: 100,
              itemsProcessed: 1,
              itemsTotal: 1,
              active: true,
            },
          };
        });
        // PR #582 live Codex finding ("Delay provenance teardown until OPEN
        // commits") + follow-up finding ("Delay provenance drop until set
        // completes") — both the provenance-ledger drop AND the
        // `openSwapCommitted` flip happen HERE, only once the synchronous
        // `set(...)` above has RETURNED (i.e. Zustand's merge already
        // applied — `geometryDocument` genuinely IS the new document).
        // `dropDocumentProvenance` was originally called just before this
        // `set(...)`, on the reasoning that reaching this line meant the
        // swap was "confirmed" — but if `syncShellStateFromMergedGeometryDocument`
        // or the audit/heal folding INSIDE the updater threw, `set(...)`
        // would propagate that throw before merging, leaving
        // `geometryDocument` on the OUTGOING document while its ledger was
        // already gone — the exact defect this PR exists to fix, in an even
        // narrower window. Moving both here (bundled — one is the ledger
        // teardown, the other is the routing-restore guard, and they must
        // agree on the same "is the swap real yet" boundary or the two
        // fixes drift apart again) closes that window: nothing below this
        // point can un-happen the swap.
        if (previousDocId !== null && previousDocId !== mergedGeometryDocument.id) {
          dropDocumentProvenance(previousDocId);
        }
        openSwapCommitted = true;
        section0MeasureSince("client:setState", tSetFile);
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // Rebuild the entity metadata index from scratch. `bulkLoad`
        // replaces the index contents, so no records from the previous
        // drawing survive — this is the "clear index" step the OPEN
        // semantics require.
        // Phase 4 Wave 2 Task 4: when streaming, the inline arrays are
        // empty — `rebuildEntityMetadataIndexFromImport` automatically
        // falls back to `snapshotSpaceEntities` for those spaces.
        // Phase 1 (П35Р) — async chunked walker.
        // Phase 4 Wave 2 (П35Р follow-up — streaming-import enable):
        //   On OPEN with streaming the inline arrays ARE empty, so the
        //   default auto-fallback works correctly. Pass `forceCacheWalk`
        //   for symmetry with the INSERT path (and so a future test that
        //   relies on the flag has the same expected behaviour here).
        await rebuildEntityMetadataIndexFromImportAsync(
          mergedGeometryDocument,
          undefined,
          { forceCacheWalk: importedViaStreaming },
        );
        // PR2 (plan §13) — build the dependency index from the opened document.
        // On the streaming OPEN branch the inline arrays are empty; the helper
        // rematerialises streamed spaces from the compact store (keyed off
        // `holder.streamedSpaceIds`, populated per-chunk by `markSpaceStreamed`).
        populateDependencyIndexFromDocument(mergedGeometryDocument);

        // EPIC-VIEWPORT-CLIP SLICE 3 — streaming-path membership finalize. The
        // worker discarded every model-space entity, so the bridge's membership
        // pass left `childEntityIds` unset; recompute it on the main thread from
        // the cache now that the streamed entities are reachable. The clipPath was
        // already resolved in the worker (objects intact + viewports retained).
        // Bump renderTopologyVersion so the viewport child pass re-reads the newly
        // populated `childEntityIds`.
        if (importedViaStreaming) {
          const populated = finalizeStreamedViewportMembership(
            mergedGeometryDocument,
            mergedGeometryDocument.id,
          );
          if (populated) {
            set((store) => ({
              renderTopologyVersion: store.renderTopologyVersion + 1,
            }));
          }
        }

        // Codex #342 F2 — pair the hydrate-IN with the export project-OUT on the
        // OPEN seam (DevTools `editorImportDxfFileAsyncReplacing`). This is the
        // store-level twin of App.tsx's file-open hydrate; idempotent if both fire.
        hydrateSysvarsFromImportedDocument(mergedGeometryDocument);
        hydrateCeColorFromImportedDocument(mergedGeometryDocument);

        // Phase 4 Wave 2 Task 4 — streaming counter wins on the
        // streaming path because `mergedGeometryDocument.spaces[].entities`
        // is empty there.
        const importedEntityCount = importedViaStreaming
          ? streamedEntityCount
          : mergedGeometryDocument.spaces.reduce(
              (count, space) => count + space.entities.length,
              0,
            );

        await new Promise<void>((resolve) => setTimeout(resolve, 120));
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));

        // i18n plural-form selection via `Intl.PluralRules`, locale-aware.
        // Codex P2 review on PR #58 flagged that the previous hand-rolled
        // mod10/mod100 rule was Russian-specific and produced "21 entity" /
        // "21 space" in the English UI (English: any value != 1 is plural).
        // The Intl API encodes the correct CLDR rules per locale.
        //
        // Mapping: Russian CLDR returns one|few|many|other. English CLDR
        // returns one|other. Existing message keys are one|few|many — we
        // collapse "two"|"other"|"zero" to "many" so English "other" lands
        // on the existing `entity.many` ("entities") / `space.many`
        // ("spaces") keys, matching the prior intent without adding more
        // keys. `pluralFormFor` is the shared one-truth helper
        // (`../i18n/pluralForm.ts`) — the Properties-panel aggregate hint
        // (`InspectorSidebar.tsx`) reuses the SAME rule instead of a second
        // hand-rolled implementation.
        const langCode = getActiveLanguage();
        const importedSpaceCount = importedGeometryDocument.spaces.length;
        const entityNounKey = `dxfio.noun.entity.${pluralFormFor(
          importedEntityCount,
          langCode,
        )}` as const;
        const spaceNounKey = `dxfio.noun.space.${pluralFormFor(
          importedSpaceCount,
          langCode,
        )}` as const;
        return {
          ok: true,
          message: t("dxfio.status.opened", {
            count: importedEntityCount,
            entityNoun: t(entityNounKey),
            spaces: importedSpaceCount,
            spaceNoun: t(spaceNounKey),
          }),
          entityCount: importedEntityCount,
          spaceCount: importedSpaceCount,
        };
      } catch (error) {
        if (currentImportHandleRef.current) {
          currentImportHandleRef.current = null;
        }
        // PR #582 live Codex finding ("Delay provenance teardown until OPEN
        // commits") — a failed/aborted parse never reaches the TRUE
        // document SWAP, so `geometryDocument` still holds the OUTGOING
        // document. But the branch above already repointed the module-
        // level `activeDocumentId` at the NEW (now-abandoned) doc id
        // before the parse started — mandatory for streaming, whose chunk
        // callback writes through `activeHolder()` while the worker is
        // still running. Restore it here so every subsequent module-state
        // read (provenance ledger, compact-cache holder) routes back to
        // the document actually on screen instead of the orphaned one.
        //
        // PR #582 follow-up Codex finding ("Do not restore routing after
        // the OPEN swap") — but ONLY when the swap itself never landed
        // (`!openSwapCommitted`). A LATER step in this same `try`
        // (metadata-index rebuild, dependency-index population, streamed-
        // viewport finalize) can throw AFTER the swap already ran — at
        // that point `geometryDocument` genuinely IS the new document, its
        // provenance ledger has already been dropped for `previousDocId`
        // via `dropDocumentProvenance`, and blindly repointing
        // `activeDocumentId` back here would route every subsequent
        // provenance/compact-cache read at the document the UI no longer
        // shows, against an already-dropped holder.
        if (previousDocId !== null && !openSwapCommitted) {
          setActiveDocument(previousDocId);
        }
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));
        const isAbort = error instanceof Error && error.name === "AbortError";
        return {
          ok: false,
          message: isAbort
            ? t("dxfio.status.aborted")
            : error instanceof Error
              ? error.message
              : String(error),
        };
      }
    },
  };
}
